import { useDashToast as useToast } from '@dashhudson/dashing-ui/toast';
import { useQueryCache, type UseInfiniteQueryData } from '@pinia/colada';
import humps from 'humps';
import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { MediaV2 } from '@/apis/queries/media-list.types';
import type { MediaListPage } from '@/app/library/composables/useMediaList.types';

import { LibraryAPI } from '@/apis';
import { QueryKeys } from '@/shared/queries';
import { useIdentityStore } from '@/stores/identity';
import { useSocketStore } from '@/stores/socket';
import { pluralize } from '@/utils/formatters';

const FAIL_SUMMARY = 'Music Muting Failed';
const FAIL_MESSAGE = "We couldn't mute the music in the selected UGC posts. Please try again.";
const NO_MUSIC_MESSAGE = 'No music to mute in the selected posts.';
const UNMUTE_FAIL_SUMMARY = 'Music Unmuting Failed';
const UNMUTE_FAIL_MESSAGE =
  "We couldn't restore the music in the selected UGC posts. Please try again.";
const MUTE_MUSIC_SCOPE = 'mute_music';
// In case a socket reply never arrives
const IDLE_TIMEOUT_MS = 5 * 60 * 1000;

interface MediaMuteEvent {
  mediaId?: number;
  scope?: string;
}

interface MuteBatch {
  brandId: number;
  pending: Set<number>;
  mutedCount: number;
  timer: ReturnType<typeof setTimeout>;
}

export const useMusicMutingStore = defineStore('musicMuting', () => {
  let addToast: ReturnType<typeof useToast>['addToast'];
  try {
    ({ addToast } = useToast({ life: 5000 }));
  } catch {
    addToast = () => '';
  }
  const queryCache = useQueryCache();
  const identityStore = useIdentityStore();
  const socketStore = useSocketStore();

  const isMuting = ref(false);
  const isUnmuting = ref(false);
  const activeBatches = new Set<MuteBatch>();

  function setMuteMusic(mediaIds: number[], brandId: number, muteMusic: boolean) {
    if (mediaIds.length === 0) return;
    const idSet = new Set(mediaIds);
    queryCache.getEntries({ key: QueryKeys.library.mediaRoot(brandId) }).forEach((entry) => {
      const data = entry.state.value.data as
        | UseInfiniteQueryData<MediaListPage, number>
        | undefined;
      if (!data?.pages) return;
      queryCache.setQueryData(entry.key, {
        pages: data.pages.map((page) => ({
          ...page,
          data: page.data.map((media) =>
            idSet.has(media.id)
              ? { ...media, muteSettings: { ...media.muteSettings, muteMusic } }
              : media,
          ),
        })),
        pageParams: data.pageParams,
      });
    });
  }

  function resolveMuteSettings(mutedMediaIds: number[], brandId: number) {
    setMuteMusic(mutedMediaIds, brandId, true);
  }

  function settleToast(mutedCount: number) {
    if (mutedCount > 0) {
      addToast({
        message: `Music muted on ${mutedCount} UGC ${pluralize(mutedCount, 'post')}`,
        severity: 'success',
      });
    } else {
      addToast({ summary: FAIL_SUMMARY, message: FAIL_MESSAGE, severity: 'error' });
    }
  }

  function settleBatch(batch: MuteBatch) {
    if (!activeBatches.delete(batch)) return;
    clearTimeout(batch.timer);
    if (batch.brandId !== identityStore.currentBrand?.id) return;
    settleToast(batch.mutedCount);
  }

  function applyMuteEvent(rawPayload: unknown, muted: boolean) {
    const { mediaId, scope } = humps.camelizeKeys(rawPayload ?? {}) as MediaMuteEvent;
    if (mediaId == null || scope !== MUTE_MUSIC_SCOPE) return;
    activeBatches.forEach((batch) => {
      if (!batch.pending.delete(mediaId)) return;
      if (muted) {
        batch.mutedCount += 1;
        resolveMuteSettings([mediaId], batch.brandId);
      }
      if (batch.pending.size === 0) {
        settleBatch(batch);
      } else {
        clearTimeout(batch.timer);
        batch.timer = setTimeout(() => settleBatch(batch), IDLE_TIMEOUT_MS);
      }
    });
  }

  // media_muted + media_mute_in_progress both count as success:
  // in-progress completes on another socket and almost always succeeds; a rare failure self-heals on refetch.
  function onMediaMuted(rawPayload: unknown) {
    applyMuteEvent(rawPayload, true);
  }

  function onMediaMuteFailed(rawPayload: unknown) {
    applyMuteEvent(rawPayload, false);
  }

  async function muteMusic(media: MediaV2[]): Promise<boolean> {
    if (media.length === 0) return true;
    const brandId = identityStore.currentBrand?.id;
    const socketId = socketStore.id;
    if (brandId == null || !socketId) {
      addToast({ summary: FAIL_SUMMARY, message: FAIL_MESSAGE, severity: 'error' });
      return false;
    }

    isMuting.value = true;
    try {
      const { data } = await LibraryAPI.bulkUpdateBrandMedia({
        brandId,
        mediaIds: media.map((m) => m.id),
        muteSettings: { muteMusic: true },
        socketId,
      });
      const alreadyMutedMediaIds = data?.muteResult?.alreadyMutedMediaIds ?? [];
      const mutingMediaIds = data?.muteResult?.mutingMediaIds ?? [];
      const enqueueFailedMediaIds = data?.muteResult?.enqueueFailedMediaIds ?? [];
      resolveMuteSettings(alreadyMutedMediaIds, brandId);

      if (mutingMediaIds.length === 0) {
        if (alreadyMutedMediaIds.length === 0 && enqueueFailedMediaIds.length === 0) {
          // Nothing eligible server-side — not a failure
          addToast({ message: NO_MUSIC_MESSAGE, severity: 'info' });
        } else {
          settleToast(alreadyMutedMediaIds.length);
        }
        return true;
      }

      addToast({
        message: `Muting music on ${mutingMediaIds.length} UGC ${pluralize(mutingMediaIds.length, 'post')}...`,
        severity: 'info',
      });
      const batch: MuteBatch = {
        brandId,
        pending: new Set(mutingMediaIds),
        mutedCount: alreadyMutedMediaIds.length,
        timer: setTimeout(() => settleBatch(batch), IDLE_TIMEOUT_MS),
      };
      activeBatches.add(batch);
      return true;
    } catch {
      addToast({ summary: FAIL_SUMMARY, message: FAIL_MESSAGE, severity: 'error' });
      return false;
    } finally {
      isMuting.value = false;
    }
  }

  // Unmute clears the flag in one PATCH — no compute, no socket, no batch; every post reverts on the response.
  async function unmuteMusic(media: MediaV2[]): Promise<boolean> {
    if (media.length === 0) return true;
    const brandId = identityStore.currentBrand?.id;
    if (brandId == null) {
      addToast({ summary: UNMUTE_FAIL_SUMMARY, message: UNMUTE_FAIL_MESSAGE, severity: 'error' });
      return false;
    }

    isUnmuting.value = true;
    try {
      const { data } = await LibraryAPI.bulkUpdateBrandMedia({
        brandId,
        mediaIds: media.map((m) => m.id),
        muteSettings: { muteMusic: false },
      });
      const unmutedMediaIds = data?.updatedMediaIds ?? [];
      if (unmutedMediaIds.length === 0) {
        addToast({ summary: UNMUTE_FAIL_SUMMARY, message: UNMUTE_FAIL_MESSAGE, severity: 'error' });
        return false;
      }
      setMuteMusic(unmutedMediaIds, brandId, false);
      addToast({
        message: `Music unmuted on ${unmutedMediaIds.length} UGC ${pluralize(unmutedMediaIds.length, 'post')}`,
        severity: 'success',
      });
      return true;
    } catch {
      addToast({ summary: UNMUTE_FAIL_SUMMARY, message: UNMUTE_FAIL_MESSAGE, severity: 'error' });
      return false;
    } finally {
      isUnmuting.value = false;
    }
  }

  return { isMuting, isUnmuting, muteMusic, unmuteMusic, onMediaMuted, onMediaMuteFailed };
});
